import {
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Provider, type InventoryResource } from '@repo/api-gw-sdk';
import { Fragment } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import CopyToClipboardButton, {
  CopyToClipboardWrapper,
} from '@/components/buttons/copyToClipboardButton';
import { volumePresets } from '@/components/volumes/volumeSettings';
import { fileSizeFormatter } from '@/utils/fileSizeFormatter';

import { SidePanelPropsTable } from './sidePanelPropsTable';

export const EKSNamespaceSection = ({
  resource,
}: {
  resource: InventoryResource;
}) => {
  const config =
    resource.resourceProperties?.awsEksNamespace ||
    resource.resourceProperties?.azureAksNamespace ||
    resource.resourceProperties?.gcpGkeNamespace;

  return (
    <Stack>
      {config?.secretsCount && (
        <SidePanelPropsTable sx={{ marginBottom: '12px' }}>
          <TableBody>
            <TableRow>
              <TableCell
                className='pb-[20px] pl-[40px]'
                sx={{
                  width: '210px',
                  verticalAlign: 'top',
                }}
              >
                Secrets count
              </TableCell>
              <TableCell className='pb-[20px]'>
                {config?.secretsCount}
              </TableCell>
            </TableRow>
            {!!config?.persistentVolumeClaims?.length && (
              <TableRow>
                <TableCell
                  className='pt-[20px] pl-[40px]'
                  sx={{
                    width: '210px',
                    verticalAlign: 'top',
                    border: 'none',
                  }}
                >
                  {resource.cloudProvider === Provider.Aws
                    ? 'Persistent volumes'
                    : 'Persistent disks'}
                </TableCell>
                <TableCell sx={{ border: 'none' }}></TableCell>
              </TableRow>
            )}
          </TableBody>
        </SidePanelPropsTable>
      )}
      <Stack
        divider={
          <Divider className='my-[12px]' sx={{ borderStyle: 'dashed' }} />
        }
      >
        {config?.persistentVolumeClaims?.map((volume) => {
          return (
            <Fragment key={volume.providerResourceId}>
              <Accordion className='px-[40px]'>
                <AccordionSummary>
                  <Typography className='font-semibold'>
                    {volume.pvcName}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className='py-[12px] pl-[36px] pr-0'>
                  <Table
                    className='w-full'
                    sx={{
                      td: {
                        padding: '4px 0',
                        borderBottom: 'none',
                        verticalAlign: 'top',
                        lineHeight: '24px',
                        height: '24px',
                      },

                      '& .props-col': {
                        fontWeight: '400',
                        width: '140px',
                      },
                    }}
                  >
                    <TableBody>
                      <TableRow>
                        <TableCell className='props-col'>
                          {resource.cloudProvider === Provider.Aws
                            ? 'Volume ID'
                            : 'Disk ID'}
                        </TableCell>
                        <TableCell>
                          <CopyToClipboardWrapper>
                            <Stack direction='row' alignItems='start' gap='8px'>
                              <Typography>
                                {volume.providerResourceId}
                              </Typography>
                              <CopyToClipboardButton
                                textToCopy={volume.providerResourceId}
                                showOnlyOnHover
                                size='small'
                              />
                            </Stack>
                          </CopyToClipboardWrapper>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className='props-col'>PV name</TableCell>
                        <TableCell>
                          <CopyToClipboardWrapper>
                            <Stack direction='row' alignItems='start' gap='8px'>
                              <Typography>{volume.pvName}</Typography>
                              <CopyToClipboardButton
                                textToCopy={volume.pvName}
                                showOnlyOnHover
                                size='small'
                              />
                            </Stack>
                          </CopyToClipboardWrapper>
                        </TableCell>
                      </TableRow>
                      {volume.availabilityZone && (
                        <TableRow>
                          <TableCell className='props-col'>
                            Availability zone
                          </TableCell>
                          <TableCell>{volume.availabilityZone}</TableCell>
                        </TableRow>
                      )}
                      {volume.fsType && (
                        <TableRow>
                          <TableCell className='props-col'>FS type</TableCell>
                          <TableCell>{volume.fsType}</TableCell>
                        </TableRow>
                      )}
                      {volume.volumeSettings.type &&
                        volumePresets[volume.volumeSettings.type] && (
                          <TableRow>
                            <TableCell className='props-col'>Type</TableCell>
                            <TableCell>
                              {
                                volumePresets[volume.volumeSettings.type]
                                  .displayName
                              }
                            </TableCell>
                          </TableRow>
                        )}
                      <TableRow>
                        <TableCell className='props-col'>Size</TableCell>
                        <TableCell>
                          {fileSizeFormatter(
                            volume.volumeSettings.sizeBytes,
                            1,
                            false
                          )}
                        </TableCell>
                      </TableRow>
                      {volume.volumeSettings.iops && (
                        <TableRow>
                          <TableCell className='props-col'>IOPS</TableCell>
                          <TableCell>{volume.volumeSettings.iops}</TableCell>
                        </TableRow>
                      )}
                      {volume.volumeSettings.throughput && (
                        <TableRow>
                          <TableCell className='props-col'>
                            Throughput
                          </TableCell>
                          <TableCell>{`${volume.volumeSettings.throughput} MB/s`}</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            </Fragment>
          );
        })}
      </Stack>
    </Stack>
  );
};
